import React, {
  FocusEvent,
  ForwardedRef,
  KeyboardEvent,
  RefObject,
  useState,
} from "react";
import {BaseInput, InputType} from "/src/types";
import {css} from "/src/utils/utils";
import Check from "/src/svg/check.svg";
import ErrorMessage from "../error-message/ErrorMessage";
import FormError from "/src/svg/form-error.svg";
import Icon from "../../icon/Icon";
import Label from "../label/Label";

import * as styles from "./input.module.scss";

interface Props extends BaseInput {
  defaultValue?: string;
  disableError?: boolean;
  inputRef?: RefObject<HTMLInputElement> | ForwardedRef<HTMLInputElement>;
  max?: number;
  maxLength?: number;
  min?: number;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onChange?: (value: string) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: KeyboardEvent) => void;
  placeholder?: string;
  transparentBg?: boolean;
  type: InputType;
}

export default function Input(props: Props) {
  const {
    darkMode = false,
    defaultValue = undefined,
    disabled = false,
    disableError = false,
    errorMessage = "",
    inputRef,
    label = "",
    max,
    maxLength,
    min,
    name,
    onBlur,
    onChange,
    onFocus,
    onKeyDown,
    placeholder = "",
    readOnly = false,
    required = false,
    status,
    transparentBg = false,
    type,
    value = undefined,
  } = props;

  const [isFocusedState, setIsFocusedState] = useState(false);

  const isInvalid = status === "error";
  const isValid = status === "success";

  const onInputFocus = (e: FocusEvent<HTMLInputElement>) => {
    // if status is not error/success, set focus state
    if (!isInvalid && !isValid) {
      setIsFocusedState(true);
    }

    if (onFocus) {
      onFocus(e);
    }
  };

  const onInputBlur = (e: FocusEvent<HTMLInputElement>) => {
    setIsFocusedState(false);

    if (onBlur) {
      onBlur(e);
    }
  };

  return type === "hidden" ? (
    <input type="hidden" name={name} value={value} />
  ) : (
    <label
      className={css(
        darkMode ? styles.rootDark : "",
        disabled ? styles.rootDisabled : "",
        isFocusedState ? styles.rootFocused : "",
        isInvalid ? styles.rootError : "",
        isValid ? styles.rootSuccess : "",
        styles.root__label,
        transparentBg ? styles.rootTransparent : ""
      )}
    >
      <Label
        label={label}
        required={required}
        darkMode={darkMode}
        disabled={disabled}
      />
      <div className={styles.root__wrapper}>
        <input
          className={css(
            "p",
            styles.root__input,
            isInvalid || isValid ? styles.root__inputPadded : ""
          )}
          data-lpignore="true"
          defaultValue={defaultValue}
          disabled={disabled}
          max={max}
          maxLength={maxLength}
          min={min}
          name={name}
          onBlur={onInputBlur}
          onChange={e => onChange && onChange(e.target.value)}
          onFocus={onInputFocus}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          readOnly={readOnly}
          ref={inputRef}
          required={required}
          type={type}
          value={value}
        />
        {/* always leave a space for icon */}
        {(isInvalid || isValid) && (
          <Icon
            role="presentation"
            viewBox="0 0 28 28"
            classes={styles.root__icon}
            svg={isValid ? Check : FormError}
          />
        )}
      </div>

      {!disableError && (
        <ErrorMessage message={errorMessage} visible={isInvalid} />
      )}
    </label>
  );
}
